/**
 * Starfleet OIDC authorization-code + PKCE (public client).
 */

import { generateCodeChallenge, generateCodeVerifier, generateOAuthState } from './starfleetPkce';

export const STARFLEET_ID_TOKEN_STORAGE_KEY = 'prhs.starfleet.id_token';
export const STARFLEET_OAUTH_STATE_KEY = 'prhs.starfleet.oauth_state';
export const STARFLEET_CODE_VERIFIER_KEY = 'prhs.starfleet.code_verifier';
// Display-only claims from /userinfo, cached because the access token they were
// read with is deliberately not kept. Tied to the id_token's lifetime: cleared
// wherever that is, so one user's name can't outlive their session.
export const STARFLEET_USERINFO_STORAGE_KEY = 'prhs.starfleet.userinfo';

const DEFAULT_SCOPE = 'openid profile email';

/**
 * @param {string} loginOrigin e.g. https://stg.login.nvidia.com
 */
export function getStarfleetTokenEndpoint(loginOrigin) {
  const base = (loginOrigin || '').replace(/\/$/, '');
  return `${base}/token`;
}

/**
 * @param {string} loginOrigin e.g. https://stg.login.nvidia.com
 */
export function getStarfleetUserInfoEndpoint(loginOrigin) {
  const base = (loginOrigin || '').replace(/\/$/, '');
  return `${base}/userinfo`;
}

/**
 * @param {object} opts
 * @param {string} opts.loginOrigin
 * @param {string} opts.clientId
 * @param {string} opts.redirectUri absolute callback URL
 * @param {string} [opts.scope]
 * @param {string} opts.loginHint
 * @param {string} opts.idpId
 */
export async function redirectToStarfleetAuthorize(opts) {
  const {
    loginOrigin,
    clientId,
    redirectUri,
    scope = DEFAULT_SCOPE,
    loginHint,
    idpId,
  } = opts;

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateOAuthState();

  sessionStorage.setItem(STARFLEET_CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(STARFLEET_OAUTH_STATE_KEY, state);

  const base = (loginOrigin || '').replace(/\/$/, '');
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (loginHint) params.set('login_hint', loginHint);
  if (idpId) params.set('idp_id', idpId);

  // Use replace() (not assign()) so the silent-login round-trip
  // (page -> /authorize -> /oauth/callback -> '/') adds NO back/forward
  // history entries — the callback already navigates home with replace:true.
  // Keeping the session history length at 1 lets a script close the tab:
  // Chromium permits window.close() when the tab has no opener and history
  // length <= 1. Our flows are opened as a fresh tab (e.g. from Jarvis via a
  // link, which is rel="noopener"), so this is what makes the module's
  // edit-mode Cancel button (window.close()) actually close the tab. It also
  // stops the Back button from replaying the login redirect.
  window.location.replace(`${base}/authorize?${params.toString()}`);
}

/**
 * @param {object} opts
 * @param {string} opts.tokenEndpoint
 * @param {string} opts.clientId
 * @param {string} opts.code
 * @param {string} opts.redirectUri
 * @param {string} opts.codeVerifier
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<{ id_token?: string, access_token?: string, refresh_token?: string }>}
 */
export async function exchangeAuthorizationCode(opts) {
  const { tokenEndpoint, clientId, code, redirectUri, codeVerifier, signal } = opts;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
    code_verifier: codeVerifier,
  });

  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
    signal,
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // leave json null
  }

  if (!res.ok) {
    const msg =
      json?.error_description ||
      json?.error ||
      text?.slice(0, 200) ||
      `Token exchange failed (${res.status})`;
    throw new Error(msg);
  }

  return json || {};
}

export function clearOAuthTransientKeys() {
  sessionStorage.removeItem(STARFLEET_OAUTH_STATE_KEY);
  sessionStorage.removeItem(STARFLEET_CODE_VERIFIER_KEY);
}

export const STARFLEET_REAUTH_ATTEMPTS_KEY = 'prhs.starfleet.reauth_attempts';
export const STARFLEET_SESSION_EXPIRED_KEY = 'prhs.starfleet.session_expired';

/**
 * How many silent /authorize round-trips we'll spend trying to recover a
 * session before giving up and telling the user.
 *
 * A silent re-auth only works while the NVIDIA session at Starfleet is itself
 * alive. Once it isn't, /authorize returns without a code, we land back here
 * with no token, and the bootstrap would redirect again — forever, since the
 * in-memory oauthRedirectIssuedRef guard resets on every page load. This
 * counter is the cross-reload half of that guard.
 */
export const MAX_SILENT_REAUTH_ATTEMPTS = 2;

export function readReauthAttempts() {
  if (typeof window === 'undefined') return 0;
  try {
    const n = parseInt(
      window.sessionStorage.getItem(STARFLEET_REAUTH_ATTEMPTS_KEY) || '0',
      10
    );
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function recordReauthAttempt() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      STARFLEET_REAUTH_ATTEMPTS_KEY,
      String(readReauthAttempts() + 1)
    );
  } catch {
    // Blocked sessionStorage means we can't count attempts. Nothing else
    // persists either, so there's no loop to break out of.
  }
}

/**
 * Give the automatic redirects their budget back.
 *
 * Deliberately does not touch the expiry marker. Spending the budget and
 * recovering the session are different events: a user-initiated retry resets the
 * former before it knows whether it will succeed, and clearing the latter at
 * that point would downgrade the screen to "sign in required" if the redirect
 * never happened. Only a token in hand proves the session is back — see
 * `clearStarfleetSessionExpired`.
 */
export function clearReauthAttempts() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STARFLEET_REAUTH_ATTEMPTS_KEY);
  } catch {
    // ignore
  }
}

/** Called once a token exchange succeeds: the session really did come back. */
export function clearStarfleetSessionExpired() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STARFLEET_SESSION_EXPIRED_KEY);
  } catch {
    // ignore
  }
}

/**
 * Remember that a session we *had* went away, so the fallback screen can say
 * "your session expired" instead of "sign in required" — the latter is a lie
 * to someone who was signed in a minute ago, and it's the only difference
 * between the two states once the token is gone.
 */
export function markStarfleetSessionExpired() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STARFLEET_SESSION_EXPIRED_KEY, '1');
  } catch {
    // ignore
  }
}

export function didStarfleetSessionExpire() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(STARFLEET_SESSION_EXPIRED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Returns true if the JWT's `exp` claim is in the past (with a small skew tolerance
 * to absorb clock differences between client and Starfleet). Missing/unparseable
 * tokens are treated as expired so callers can short-circuit safely.
 *
 * @param {string} token
 * @param {number} [skewSeconds=30]
 * @returns {boolean}
 */
export function isStarfleetIdTokenExpired(token, skewSeconds = 30) {
  if (!token) return true;
  const p = parseJwtPayloadUnsafe(token);
  if (!p || typeof p.exp !== 'number') return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return p.exp <= nowSec + skewSeconds;
}

export function readStoredStarfleetIdToken() {
  if (typeof window === 'undefined') return null;
  const token = sessionStorage.getItem(STARFLEET_ID_TOKEN_STORAGE_KEY);
  if (!token) return null;
  if (isStarfleetIdTokenExpired(token)) {
    // Self-clean expired tokens so the OAuth bootstrap effect (which checks for
    // sessionStorage emptiness) will re-fire silent login on the next read or page load.
    sessionStorage.removeItem(STARFLEET_ID_TOKEN_STORAGE_KEY);
    markStarfleetSessionExpired();
    // The name cached alongside it describes the session that just expired, so
    // it goes at the same moment — otherwise it could label the next one.
    clearStoredStarfleetUserInfo();
    return null;
  }
  return token;
}

export function writeStoredStarfleetIdToken(idToken) {
  if (typeof window === 'undefined') return;
  if (idToken) {
    sessionStorage.setItem(STARFLEET_ID_TOKEN_STORAGE_KEY, idToken);
  } else {
    sessionStorage.removeItem(STARFLEET_ID_TOKEN_STORAGE_KEY);
    // The cached name describes the session that just ended.
    clearStoredStarfleetUserInfo();
  }
}

// The only claims worth keeping: enough to label the header, and nothing that
// would turn this cache into a store of personal data we have no use for.
const DISPLAY_CLAIMS = [
  'name',
  'given_name',
  'family_name',
  'nickname',
  'preferred_username',
];

const str = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * A handle counts as a name only when it isn't the address over again. Other
 * IdPs put the email in `preferred_username`, and promoting that to the name
 * line prints the address as the name and then drops the line beneath it.
 */
function nameLikeHandle(value) {
  const handle = str(value);
  return handle && !handle.includes('@') ? handle : '';
}

/**
 * The best human name these claims can offer, or '' when the only thing left
 * to show the user is their email address.
 *
 * `preferred_username` counts. For accounts federated from an external IdP,
 * Starfleet's /userinfo returns no `name`, `given_name` or `family_name` at
 * all — the name the account was registered with arrives as
 * `preferred_username` ("Cenk Pekiner 114"), so treating that claim as a mere
 * fallback behind the email is what keeps a perfectly good name off screen.
 *
 * @param {(key: string) => string} claim reads one claim as a trimmed string
 */
function pickDisplayName(claim) {
  const combined = [claim('given_name'), claim('family_name')].filter(Boolean).join(' ');
  return (
    claim('name') ||
    combined ||
    nameLikeHandle(claim('preferred_username')) ||
    nameLikeHandle(claim('nickname'))
  );
}

// Stands in when the token names nobody at all. Shared with the initial below
// so the avatar can tell a placeholder from a real identity — otherwise "signed
// in" would put a confident "S" on screen for a user we cannot name.
const SIGNED_IN_PLACEHOLDER = 'signed in';

/**
 * The avatar letter: the first character of whatever the header shows, upper
 * cased.
 *
 * Spread rather than [0] so a name starting outside the BMP isn't cut in half
 * mid-surrogate, and re-taken after casing because a few letters grow when
 * upper cased (German ß becomes SS).
 */
function initialOf(primary) {
  const first = [...primary][0];
  if (!first) return '';
  return [...first.toLocaleUpperCase()][0];
}

/**
 * True when these claims can produce a human name, i.e. anything better than
 * falling back to the email address.
 *
 * @param {object|null} claims
 */
function hasDisplayName(claims) {
  if (!claims || typeof claims !== 'object') return false;
  return Boolean(pickDisplayName((key) => str(claims[key])));
}

/**
 * Whether the id_token already names the user, i.e. whether a /userinfo call
 * would tell us anything the header doesn't already know. Claims are not
 * verified; this only decides whether to spend a request.
 *
 * @param {string} idToken
 */
export function idTokenHasDisplayName(idToken) {
  return hasDisplayName(parseJwtPayloadUnsafe(idToken));
}

export function readStoredStarfleetUserInfo() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STARFLEET_USERINFO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredStarfleetUserInfo(claims) {
  if (typeof window === 'undefined') return;
  const kept = {};
  for (const key of DISPLAY_CLAIMS) {
    if (typeof claims?.[key] === 'string' && claims[key].trim()) {
      kept[key] = claims[key].trim();
    }
  }
  if (Object.keys(kept).length === 0) return;
  try {
    sessionStorage.setItem(STARFLEET_USERINFO_STORAGE_KEY, JSON.stringify(kept));
  } catch {
    // A full or unavailable sessionStorage costs us a name, nothing more.
  }
}

export function clearStoredStarfleetUserInfo() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STARFLEET_USERINFO_STORAGE_KEY);
}

/**
 * Read the OIDC /userinfo claims that belong to `idToken`.
 *
 * Only worth calling when the id_token carries no name of its own — Starfleet
 * doesn't advertise `claims_supported`, so whether `name` rides in the token or
 * only here is a per-deployment answer. Resolves to null on any failure: a
 * missing name costs the header a nicety, and must never cost the user a login.
 *
 * The caller holds the access token for the length of this call and then drops
 * it. It is never persisted — it is a bearer credential with a far wider reach
 * than the id_token, and the header only needs the answer, not the key.
 *
 * Takes the whole id_token rather than a `sub` so the subject check below can't
 * be skipped by a caller that forgets to pass one.
 *
 * @param {object} opts
 * @param {string} opts.userInfoEndpoint
 * @param {string} opts.accessToken
 * @param {string} opts.idToken the token the claims must belong to
 * @param {AbortSignal} [opts.signal]
 * @param {number} [opts.timeoutMs] cap on the delay added to sign-in
 * @returns {Promise<object|null>}
 */
export async function fetchStarfleetUserInfo(opts) {
  const { userInfoEndpoint, accessToken, idToken, signal, timeoutMs = 2500 } = opts;
  if (!userInfoEndpoint || !accessToken) return null;

  // Nothing to bind the response to means nothing to trust it against.
  const expectedSub = parseJwtPayloadUnsafe(idToken)?.sub;
  if (typeof expectedSub !== 'string' || !expectedSub) return null;

  // Already cancelled: the listener below would never fire, leaving the request
  // to run until the local timeout instead of not running at all.
  if (signal?.aborted) return null;

  // A local controller rather than AbortSignal.any/timeout, which are recent
  // enough to be missing from browsers this app still supports.
  const controller = new AbortController();
  const onOuterAbort = () => controller.abort();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  signal?.addEventListener('abort', onOuterAbort, { once: true });

  try {
    const res = await fetch(userInfoEndpoint, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || typeof json !== 'object') return null;
    // OIDC requires the response's `sub` to match the id_token's, and requires
    // it to be present. The id_token is signed and this response is not, so
    // without the match a wrong or swapped-in payload would put another
    // person's name on this session. Mismatch is discarded outright rather
    // than trusted in part.
    if (json.sub !== expectedSub) return null;
    return json;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }
}

/**
 * Email claim from an id_token (unsigned decode). Empty/missing → null.
 * @param {string} idToken
 * @returns {string|null}
 */
export function getStarfleetIdTokenEmail(idToken) {
  const p = parseJwtPayloadUnsafe(idToken);
  if (!p || typeof p.email !== 'string') return null;
  const email = p.email.trim();
  return email || null;
}

/**
 * Subject claim from an id_token (unsigned decode). Empty/missing → null.
 * @param {string} idToken
 * @returns {string|null}
 */
export function getStarfleetIdTokenSubject(idToken) {
  const p = parseJwtPayloadUnsafe(idToken);
  if (!p || typeof p.sub !== 'string') return null;
  const sub = p.sub.trim();
  return sub || null;
}

/**
 * When UAS (or another launcher) hands PRHS a `login_hint`, a leftover
 * `sessionStorage` id_token from a previous account must not win.
 * DMO logout does not clear PRHS tab storage, so without this check the SPA
 * keeps calling `GET /v2/is-phone-required` as the prior user.
 *
 * @param {string} [loginHint] email from the launch URL; no-op when empty
 * @returns {boolean} true if a mismatched (or email-less) token was cleared
 */
export function clearStoredStarfleetIdTokenIfLoginHintMismatch(loginHint) {
  const expected = typeof loginHint === 'string' ? loginHint.trim() : '';
  if (!expected) return false;

  const token = readStoredStarfleetIdToken();
  if (!token) return false;

  const email = getStarfleetIdTokenEmail(token);
  if (email && email.toLowerCase() === expected.toLowerCase()) {
    return false;
  }

  writeStoredStarfleetIdToken(null);
  return true;
}

/**
 * Decodes JWT payload (OIDC id_token) for display only — signature is not verified.
 * @param {string} jwt
 * @returns {object|null}
 */
function parseJwtPayloadUnsafe(jwt) {
  if (!jwt || typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Short label for header UI (email, name, or fallback). Claims are not verified.
 * @param {string} idToken
 * @returns {string}
 */
export function getStarfleetAuthDisplayLabel(idToken) {
  const { primary, secondary } = getStarfleetAuthDisplayProfile(idToken);
  if (!secondary) return primary;
  return `${primary} · ${secondary}`;
}

/**
 * Two-line profile for header UI (claims not verified).
 *
 * Primary: the user's human display name — the OIDC `name` claim, else
 * `given_name`/`family_name` combined, else a `preferred_username` / `nickname`
 * that reads as a name rather than an address (see pickDisplayName). Only when
 * none of those exist does it fall back to the email, and past that to an
 * address-shaped handle or `sub`. Case is preserved, not lowercased.
 *
 * Secondary: the full email, shown beneath the primary — omitted when it would
 * just duplicate the primary (i.e. when email is itself the fallback primary).
 *
 * Initial: the avatar letter, or '' when we could not name the user at all, in
 * which case the header draws its generic person icon instead of a letter.
 *
 * `extraClaims` fills gaps only: the id_token is signed and these are not, so
 * anything present in the token wins. In practice this carries the /userinfo
 * name for deployments whose id_token doesn't include one.
 *
 * @param {string} idToken
 * @param {object|null} [extraClaims]
 * @returns {{ primary: string, secondary: string, initial: string }}
 */
export function getStarfleetAuthDisplayProfile(idToken, extraClaims = null) {
  const claims = parseJwtPayloadUnsafe(idToken);
  if (!claims || typeof claims !== 'object') {
    return { primary: SIGNED_IN_PLACEHOLDER, secondary: '', initial: '' };
  }
  // Per field rather than a merged object, so a claim the token sends as an
  // empty string doesn't shadow a real value from /userinfo.
  const claim = (key) => str(claims[key]) || str(extraClaims?.[key]);

  const email = claim('email');
  const nickname = claim('nickname');
  const preferred = claim('preferred_username');
  const sub = str(claims.sub);

  // Prefer a real display name; the email is a fallback only.
  const displayName = pickDisplayName(claim);

  let primary;
  if (displayName) primary = displayName;
  else if (email) primary = email;
  // Only reachable for an address-shaped handle with no email claim to prefer:
  // pickDisplayName already took any handle that reads as a name.
  else if (preferred) primary = preferred;
  else if (nickname) primary = nickname;
  else if (sub) primary = sub.length > 12 ? `${sub.slice(0, 12)}…` : sub;
  else primary = SIGNED_IN_PLACEHOLDER;

  // Email belongs on the secondary line, unless it's already the primary.
  const secondary = email && email !== primary ? email : '';

  return {
    primary,
    secondary,
    initial: primary === SIGNED_IN_PLACEHOLDER ? '' : initialOf(primary),
  };
}
